import { AnimatePresence, motion } from "framer-motion";
import {
  BookOpen,
  Check,
  Copy,
  Download,
  Edit,
  FileText,
  Globe,
  HelpCircle,
  Highlighter,
  MessageCircle,
  MoreHorizontal,
  Search,
  Sparkles,
  Tag,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { AnnotationRange } from "../../types/api";

export interface SelectionState {
  rect: { top: number; bottom: number; left: number; width: number };
  text: string;
  annotationRange?: AnnotationRange;
  sectionId?: string;
}

interface SelectionMenuProps {
  rect: SelectionState["rect"];
  onAction: (kind: string, extra?: string) => void;
  onClose: () => void;
}

const COLORS = ["yellow", "green", "pink", "blue", "purple"] as const;
const LANGS = ["EN", "FR", "DE", "PT", "IT"] as const;

export function SelectionMenu({ rect, onAction, onClose }: SelectionMenuProps) {
  const [submenu, setSubmenu] = useState<"color" | "lang" | "more" | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  // Position above the selection by default; flip below if no room
  const top = rect.top > 60 ? rect.top - 46 : rect.bottom + 8;
  const left = rect.left + rect.width / 2;

  return (
    <AnimatePresence>
      <motion.div
        ref={ref}
        initial={{ opacity: 0, scale: 0.95, y: 4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 4 }}
        transition={{ duration: 0.12 }}
        style={{
          position: "fixed",
          top: `${top}px`,
          left: `${left}px`,
          transform: "translateX(-50%)",
          zIndex: 60,
        }}
        className="rounded-lg border border-(--border-subtle) bg-(--bg-overlay) p-1 shadow-(--elev-2) backdrop-blur"
        data-selection-menu="true"
        role="menu"
        aria-label="Acciones sobre la selección"
      >
        {submenu === "color" ? (
          <div className="flex items-center gap-1 p-1">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onAction("highlight", c)}
                className="grid h-7 w-7 place-items-center rounded-md border border-transparent hover:border-(--border-strong)"
                aria-label={`Subrayar en ${c}`}
                style={{ background: `var(--highlight-${c})` }}
              />
            ))}
            <button
              type="button"
              className="ml-1 px-2 text-xs text-(--text-tertiary) hover:text-(--text-primary)"
              onClick={() => setSubmenu(null)}
            >
              ←
            </button>
          </div>
        ) : submenu === "lang" ? (
          <div className="flex items-center gap-1 p-1" data-testid="selection-submenu-lang">
            {LANGS.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => onAction("translate", l)}
                data-testid={`selection-lang-${l}`}
                className="rounded-md px-2 py-1 text-xs font-mono hover:bg-(--bg-hover)"
              >
                {l}
              </button>
            ))}
            <button
              type="button"
              className="ml-1 px-2 text-xs text-(--text-tertiary) hover:text-(--text-primary)"
              onClick={() => setSubmenu(null)}
            >
              ←
            </button>
          </div>
        ) : submenu === "more" ? (
          <div className="flex items-center gap-0.5 p-1" data-testid="selection-submenu-more">
            <Action
              label="Buscar similares"
              icon={<Search size={13} />}
              onClick={() => onAction("search-similar")}
            />
            <Action
              label="Crear checklist"
              icon={<Check size={13} />}
              onClick={() => onAction("create-checklist")}
            />
            <Action
              label="Crear doc"
              icon={<FileText size={13} />}
              onClick={() => onAction("create-doc")}
            />
            <Action
              label="Proponer edición"
              icon={<Edit size={13} />}
              onClick={() => onAction("propose-edit")}
            />
            <Action
              label="Copiar MD"
              icon={<Copy size={13} />}
              onClick={() => onAction("copy-md")}
            />
            <Action
              label="Descargar .md"
              icon={<Download size={13} />}
              onClick={() => onAction("download-md")}
            />
            <button
              type="button"
              className="ml-1 px-2 text-xs text-(--text-tertiary) hover:text-(--text-primary)"
              onClick={() => setSubmenu(null)}
            >
              ←
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-0.5">
            <Action
              label="Subrayar"
              icon={<Highlighter size={13} />}
              onClick={() => setSubmenu("color")}
              hotkey="⌘H"
            />
            <Action
              label="Comentar"
              icon={<MessageCircle size={13} />}
              onClick={() => onAction("comment")}
              hotkey="⌘,"
            />
            <Action
              label="Etiquetar"
              icon={<Tag size={13} />}
              onClick={() => onAction("tag")}
              hotkey="⌘L"
            />
            <Action
              label="Copiar"
              icon={<Copy size={13} />}
              onClick={() => onAction("copy")}
              hotkey="Ctrl+C"
            />
            <Action
              label="→ Apuntes"
              icon={<BookOpen size={13} />}
              onClick={() => onAction("notebook")}
              hotkey="⌘N"
            />
            <div className="mx-1 h-5 w-px bg-(--border-subtle)" />
            <Action
              label="Traducir"
              icon={<Globe size={13} />}
              onClick={() => setSubmenu("lang")}
              hotkey="⌘T"
            />
            <Action
              label="Resumir"
              icon={<Sparkles size={13} />}
              onClick={() => onAction("summarize")}
              hotkey="⌘R"
            />
            <Action
              label="Explicar"
              icon={<HelpCircle size={13} />}
              onClick={() => onAction("explain")}
              hotkey="⌘E"
            />
            <Action
              label="Más"
              icon={<MoreHorizontal size={13} />}
              onClick={() => setSubmenu("more")}
              testId="selection-more"
            />
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

function Action({
  label,
  icon,
  onClick,
  hotkey,
  testId,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  hotkey?: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={hotkey ? `${label} (${hotkey})` : label}
      aria-label={label}
      data-testid={testId}
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-(--text-primary) hover:bg-(--bg-hover)"
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
